import {CacheKeyConfig, useGrpcRequest} from '@aboutyou/data-fetcher';
import {Session} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/session.types';
import {WishlistState} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/wishlist.types';
import {WishlistPageService_GetWishlistPageLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/wishlist/v1/page/service.grpc.lazy';
import {GetWishlistPageResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/wishlist/v1/page/service.types';
import {
  useBasketToken,
  useFullSession,
  useRemoveProductFromWishlistMutation,
  useUserEmail,
  useWishlistMinimalState,
  useWishlistTokenHash,
} from '@aboutyou/user';
import React, {createContext, useCallback, useContext, useEffect} from 'react';
import {useSorting} from '../../../features/ProductStream/utils';
import {useReductionsStateRequestParameter} from '../../../features/ReductionsState/useReductionsStateRequestParameter';
import {useFirstProductIds} from '../../../features/firstProductId/useFirstProductIds';
import {useTargetGroupIdentifier} from '../../../shared-deprecated/useTargetGroupIdentifier';
import {mapSortToWishlistSort} from '../helpers';
import {getWishlistItemProductId} from '../utils';
// eslint-disable-next-line jest/no-mocks-import
import {
  DealsTabValue,
  useDealsTabsOpenedState,
} from './useDealsTabsOpenedState';

const useModifyWishlistData = ({
  data,
  refetch,
  updateCache,
  setWishlistTokenHash,
}: {
  data?: GetWishlistPageResponse;
  refetch: VoidFunction;
  updateCache: (data: GetWishlistPageResponse | undefined) => void;
  setWishlistTokenHash: (hash: string) => void;
}) => {
  const {parameter} = useDealsTabsOpenedState();

  const removeItemFromWishlist =
    useRemoveProductFromWishlistMutation(setWishlistTokenHash);

  const deleteItem = useCallback(
    async (productId: number) => {
      try {
        const items:
          | GetWishlistPageResponse['allItemsTab']
          | GetWishlistPageResponse['discountedItemsTab'] =
          parameter === DealsTabValue.all
            ? data?.allItemsTab
            : data?.discountedItemsTab;

        if (items && data) {
          const allItemsFiltered = data.allItemsTab.items.filter(
            item =>
              getWishlistItemProductId(item) === undefined ||
              getWishlistItemProductId(item) !== productId,
          );
          const allItemsRemoved =
            data.allItemsTab.items.length !== allItemsFiltered.length;
          data.allItemsTab = {
            ...data.allItemsTab,
            items: allItemsFiltered,
            productCount:
              data.allItemsTab.productCount - (allItemsRemoved ? 1 : 0),
          };

          // we need to make sure item is deleted from both arrays
          if (data.discountedItemsTab) {
            let itemWasRemoved = false;
            data.discountedItemsTab.items =
              data.discountedItemsTab.items.filter(item => {
                const id = getWishlistItemProductId(item);
                const returnValue = id === undefined || id !== productId;
                if (!returnValue) {
                  itemWasRemoved = true;
                }
                return returnValue;
              });
            if (itemWasRemoved) {
              data.discountedItemsTab.productCount =
                data.discountedItemsTab.productCount - 1;
            }
          }

          updateCache({...data});
        }

        return await removeItemFromWishlist.mutateAsync({productId});
      } catch (e) {
        removeItemFromWishlist.reset();
        refetch();
      }
    },
    [
      removeItemFromWishlist,
      data?.allItemsTab.items.length,
      data?.discountedItemsTab?.items.length,
      parameter,
    ],
  );

  return {data, deleteItem};
};

export function useWishlistPageStream(
  {disabled}: {disabled: boolean} = {disabled: false},
) {
  const {request, setWishlistTokenHashState} = useWishlistPageRequest(disabled);

  const {data, deleteItem} = useModifyWishlistData({
    data: request.data,
    refetch: request.refetch,
    updateCache: request.updateCache,
    setWishlistTokenHash: setWishlistTokenHashState,
  });

  const optimisticRequest = {...request, data};
  return {loadItems: optimisticRequest, deleteItem};
}

const useWishlistPageRequest = (disabled: boolean) => {
  const targetGroup = useTargetGroupIdentifier();
  const {sorting} = useSorting();
  const userEmail = useUserEmail();

  const {token: basketToken} = useBasketToken();
  const session = useFullSession();
  const firstProductIds = useFirstProductIds();

  const wishlistMinimalState = useWishlistMinimalState();
  const wishlistTokenHash = useWishlistTokenHash();
  /**
   * We need to ref this token has because we want to prevent refetches on the wishlist
   * when you modify it on the wishlist. If its modified and you come back to it when data
   * is already there, the ref will be initialized newly and the token will be different
   * and therefore the page will be rerequested
   */
  const [wishlistTokenHashState, setWishlistTokenHashState] =
    React.useState(wishlistTokenHash);

  // if wishlist state runs into an error, we should fallback to null to not block the wishlist
  const wishlistToken = wishlistMinimalState.error
    ? null
    : wishlistMinimalState.data?.wishlistState.token;

  /**
   * GetWishlistPage request should be made only after the GetWishlistState request is done
   * Otherwise SAPI will return 423 (wishlist is locked) error when requesting both at the same time
   */
  const isWishlistStateLoading =
    wishlistMinimalState.isLoading ||
    wishlistMinimalState.isFetching ||
    wishlistMinimalState.isStale;

  const params = {
    session: session as Session,
    /**
     * We will go with static ones because we removed the reco lanes for the webstacks completely for now
     * we might come back and restore them later on
     */
    lastSeenProductIds: [],
    basketProductIds: [],
    targetGroup,
    reductionsState: useReductionsStateRequestParameter(),
    sortOptions: mapSortToWishlistSort(sorting),
    firstProductIds,
    basketToken,
    wishlistToken: wishlistToken ?? null,
    email: userEmail ?? null,
  };

  const wishlistMinimalStateReady =
    (wishlistMinimalState.data && !isWishlistStateLoading) ||
    wishlistMinimalState.error;

  const isRequestDisabled = !session || disabled || !wishlistMinimalStateReady;

  const request = useGrpcRequest(
    WishlistPageService_GetWishlistPageLazy,
    params,
    {
      disabled: isRequestDisabled,
      refetchToken: wishlistTokenHashState || undefined,
      transformCacheKey: (config: CacheKeyConfig) => {
        return {
          ...config,
          params: {
            ...config.params,
            basketToken: undefined,
            wishlistToken: undefined,
          },
        };
      },
      onSuccess: data => {
        wishlistMinimalState.updateCache(data);
      },
    },
  );

  const wishlistDataCount = request.data?.allItemsTab.items.length;
  const wishlistStateCount =
    wishlistMinimalState.data?.wishlistState.items.length;

  /**
   * This effect is used to change the refetch token only when the wishlist count
   * goes up and also when the token hash changes. This means that also
   * the minimal state request has finished
   */
  useEffect(() => {
    if (disabled) {
      return;
    }

    // initialization of the state
    if (!wishlistTokenHashState && wishlistTokenHash) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setWishlistTokenHashState(wishlistTokenHash);
      return;
    }

    if (
      wishlistTokenHash === undefined ||
      wishlistTokenHashState === undefined
    ) {
      return;
    }

    if (wishlistTokenHash === wishlistTokenHashState) {
      return;
    }

    if (wishlistStateCount === undefined || wishlistDataCount === undefined) {
      return;
    }

    if (wishlistStateCount > wishlistDataCount) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setWishlistTokenHashState(wishlistTokenHash);
    }
  }, [
    disabled,
    wishlistDataCount,
    wishlistStateCount,
    wishlistTokenHash,
    wishlistTokenHashState,
  ]);

  return {request, setWishlistTokenHashState};
};

type DeleteItemFunction = (
  productId: number,
) => Promise<WishlistState | undefined>;
const deleteItemContext = createContext<DeleteItemFunction>(() =>
  Promise.reject(
    new Error(
      'Wishlist delete item handler was not found, did you miss to wrap the DeleteItemProvider?',
    ),
  ),
);

/**
 * This is a pure performance enhancement: we add the delete item function into a context so we don't need to re-render all product tiles but only the components that call remove from wishlist.
 * The main issue is that the mutation creates a new instance for every render. Preventing this is extremely hard right now, therefore this is the better compromise at the moment.
 */
export const DeleteItemProvider: React.FC<
  React.PropsWithChildren<{deleteItem: DeleteItemFunction}>
> = ({children, deleteItem}) => {
  return (
    <deleteItemContext.Provider value={deleteItem}>
      {children}
    </deleteItemContext.Provider>
  );
};

export const useDeleteWishlistPageItem = () => useContext(deleteItemContext);
